import createDebug from 'debug';

/**
 * Namespaced debug loggers (EID-64).
 *
 * Wraps the `debug` package so hot-path diagnostics are silent in production
 * builds: a logger only emits — and, crucially, only evaluates/retains its
 * arguments — when its namespace is enabled via `localStorage.debug` in the
 * browser or the `DEBUG` env var in Node (e.g. `localStorage.debug =
 * 'eidos:*'`). With no namespace enabled the call is a near-no-op, so passing
 * large objects (data registries, vega specs, event payloads) no longer forces
 * a `console.log` to keep them alive every time the hot path runs.
 *
 * Prefer `debugFn(() => [..expensiveArgs])` on truly hot paths so the argument
 * array is never even constructed unless the namespace is enabled.
 */

export type DebugLogger = createDebug.Debugger & {
  /**
   * Lazily log: `producer` is only invoked when the namespace is enabled, so
   * the (potentially expensive) argument array is never built in production.
   */
  lazy: (producer: () => unknown[]) => void;
};

const withLazy = (logger: createDebug.Debugger): DebugLogger => {
  const extended = logger as DebugLogger;
  extended.lazy = (producer: () => unknown[]) => {
    if (extended.enabled) extended(...producer());
  };
  return extended;
};

/**
 * Create a namespaced debug logger under the `eidos:` root namespace.
 *
 * @param namespace short scope name, e.g. `'event-bus'` → `eidos:event-bus`.
 */
export const createLogger = (namespace: string): DebugLogger =>
  withLazy(createDebug(`eidos:${namespace}`));
